import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { debounce } from "lodash";
import React, { useState, useEffect, useCallback } from "react";
import { Button, useDataProvider, useTranslate } from "react-admin";
import { useGetIdentity } from "react-admin";
import { Link } from "react-router-dom";
import { flags } from "../../debate/translation_entries/TranslationList";
import SectionTitle from "../../utils/SectionTitle";
import classes from "./ModerationLobby.module.scss";

const ModerationLobby = () => {
	const [total, setTotal] = useState(0);
	const [lockedContents, setLockedContents] = useState(0);
	const [reported, setReported] = useState(0);
	const [totalLoading, setTotalLoading] = useState(true);
	const [lockedContentLoading, setLockedContentLoading] = useState(true);
	const [reportedLoading, setReportedLoading] = useState(true);
	const [currentLang, setCurrentLang] = useState("");
	const [groupId, setGroupId] = useState("");
	const [groupOptions, setGroupOptions] = useState([]);
	const [openGroupOptions, setOpenGroupOptions] = useState(false);
	const [groupOptionsInput, setGroupOptionsInput] = useState("");
	const [groupType, setGroupType] = useState("");
	const [moderableType, setModerableType] = useState("");
	const [isReported, setIsReported] = useState("");
	const groupOptionsLoading = openGroupOptions && groupOptions.length === 0;
	const { identity } = useGetIdentity();
	const translate = useTranslate();
	const dataProvider = useDataProvider();

	// Moderation stack for current user
	const fetchModerationOverview = async (lang = currentLang) => {
		setTotal(0);
		setTotalLoading(true);
		const identity = JSON.parse(localStorage.getItem("currentUser"));
		try {
			const response = await dataProvider.getList("moderation_entries", {
				sort: { field: "created_at", order: "DESC" },
				pagination: { page: 1, perPage: 1 },
				filter: {
					active_entries: identity.id,
					language: lang,
					group_type: groupType,
					moderable_type: moderableType === "Report" ? "" : moderableType,
					group_id: groupId,
				},
			});
			const total = response.total;
			if (total) {
				setTotal(total);
			}
			setTotalLoading(false);
		} catch (error) {
			console.log(error);
			setTotalLoading(false);
		}
	};

	const fetchGroupOptions = useCallback(
		debounce(async (query) => {
			try {
				const response = await dataProvider.getList("groups", {
					sort: { field: "created_at", order: "DESC" },
					pagination: { page: 1, perPage: 10 },
					filter: {
						countless: true,
						is_published: true,
						...(query && { query: query }),
					},
				});
				setGroupOptions(response.data);
			} catch (error) {
				console.log(error);
			}
		}, 1000),
		[],
	);

	const fetchLockedContents = async (lang = currentLang) => {
		setLockedContents(0);
		setLockedContentLoading(true);
		try {
			const response = await dataProvider.getList("moderation_entries", {
				sort: { field: "created_at", order: "DESC" },
				pagination: { page: 1, perPage: 1 },
				filter: {
					status: 1,
					is_locked: true,
					language: lang,
					moderable_type: moderableType === "Report" ? "" : moderableType,
					group_type: groupType,
					group_id: groupId,
				},
			});
			const total = response.total;
			if (total) {
				setLockedContents(total);
			}
			setLockedContentLoading(false);
		} catch (error) {
			setLockedContentLoading(false);
			console.log(error);
		}
	};

	const fetchReportedContent = async (lang = currentLang) => {
		setReported(0);
		setReportedLoading(true);
		try {
			const response = await dataProvider.getList("moderation_entries", {
				sort: { field: "created_at", order: "DESC" },
				pagination: { page: 1, perPage: 1 },
				filter: {
					is_reported: true,
					language: lang,
					moderable_type: moderableType === "Report" ? "" : moderableType,
					group_type: groupType,
					group_id: groupId,
				},
			});
			const total = response.total;
			if (total) {
				setReported(total);
			}
			setReportedLoading(false);
		} catch (error) {
			setReportedLoading(false);
			console.log(error);
		}
	};

	const resetFilters = () => {
		setGroupId("");
		setGroupOptionsInput("");
		setGroupType("");
		setModerableType("");
		setCurrentLang("");
		setIsReported("");
	};

	useEffect(() => {
		if (openGroupOptions) {
			fetchGroupOptions();
		}
	}, [openGroupOptions, fetchGroupOptions]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: dependencies rerender too often
	useEffect(() => {
		setLockedContents(0);
		setTotal(0);
		if (moderableType !== "Report") {
			fetchModerationOverview(currentLang);
			fetchLockedContents(currentLang);
		}
		fetchReportedContent(currentLang);
	}, [currentLang, moderableType]);

	useEffect(() => {
		if (moderableType === "Comment") {
			setModerableType("Message");
			setGroupType("Source");
		}
		if (moderableType === "Report") {
			setModerableType("Report");
			setIsReported(true);
		}
		if (moderableType === "DebateSuggestion") {
			setModerableType("Group");
		}
	}, [moderableType]);

	return (
		<>
			<SectionTitle
				title={translate("resources.moderation_entries.label.name")}
			/>
			<div className={classes.root}>
				<div className={classes.header}>
					<div className={classes.stats}>
						{totalLoading ? (
							<CircularProgress />
						) : (
							<span className={classes.statNumber}> {total} </span>
						)}
						{translate(
							"resources.moderation_entries.label.content_to_moderate",
						)}
					</div>
					<div className={classes.stats}>
						{lockedContentLoading ? (
							<CircularProgress />
						) : (
							<span className={classes.statNumber}> {lockedContents} </span>
						)}
						{translate("resources.moderation_entries.label.content_available")}
					</div>
					<div className={classes.stats}>
						{reportedLoading ? (
							<CircularProgress />
						) : (
							<span className={classes.statNumber}> {reported} </span>
						)}
						{translate("resources.moderation_entries.label.reported_content", {
							smart_count: reported,
						})}
					</div>
				</div>
				<div className={classes.actions}>
					{identity?.settings?.translation?.enable === true &&
						identity?.settings?.translation?.translationMethods &&
						Object.keys(identity?.settings?.translation?.translationMethods)
							.length > 0 && (
							<FormControl
								sx={{
									marginTop: "15px",
									width: "max-content",
									minWidth: "200px",
									borderColor: "#5B7FF1",
									marginRight: "15px",
								}}
							>
								<InputLabel id="currentLang-label">
									{translate("resources.moderation_entries.label.language")}
								</InputLabel>
								<Select
									id="currentLang"
									value={currentLang}
									className={classes.selectLang}
									label="Language"
									onChange={(e) => setCurrentLang(e.target.value)}
								>
									<MenuItem value="">
										<em>
											{translate(
												"resources.moderation_entries.label.all_languages",
											)}
										</em>
									</MenuItem>
									{Object.keys(
										identity.settings.translation.translationMethods,
									).map((lang) => (
										<MenuItem key={lang} value={lang}>
											{lang.toUpperCase()} {flags[lang]}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
					<FormControl
						sx={{
							marginTop: "15px",
							width: "max-content",
							minWidth: "200px",
							borderColor: "#5B7FF1",
							marginRight: "15px",
						}}
					>
						<InputLabel id="moderableType-label">
							{translate("resources.moderation_entries.label.moderable_type")}
						</InputLabel>
						<Select
							id="moderableType"
							value={moderableType}
							className={classes.selectLang}
							label={translate(
								"resources.moderation_entries.label.content_type",
							)}
							onChange={(e) => setModerableType(e.target.value)}
						>
							<MenuItem value="" key="default">
								<em>
									{translate("resources.moderation_entries.label.all_types")}
								</em>
							</MenuItem>
							<MenuItem value={"Message"} key="Message">
								{translate("resources.moderation_entries.type.message")}
							</MenuItem>
							<MenuItem value={"DebateSuggestion"} key="DebateSuggestion">
								{translate(
									"resources.moderation_entries.type.debate_suggestion",
								)}
							</MenuItem>
							<MenuItem value={"User"} key="User">
								{translate("resources.moderation_entries.type.user")}
							</MenuItem>
							<MenuItem value={"Proposal"} key="Proposal">
								{translate("resources.moderation_entries.type.proposal")}
							</MenuItem>
							<MenuItem value={"Comment"} key="Comment">
								{translate("resources.moderation_entries.type.comment")}
							</MenuItem>
							<MenuItem value={"Report"} key="Report">
								{translate("resources.moderation_entries.type.report")}
							</MenuItem>
						</Select>
					</FormControl>
					<FormControl
						sx={{
							marginTop: "15px",
							width: "max-content",
							minWidth: "200px",
							borderColor: "#5B7FF1",
							marginRight: "15px",
						}}
					>
						<Autocomplete
							freeSolo
							forcePopupIcon
							disablePortal
							disableClearable
							loading={groupOptionsLoading}
							options={groupOptions}
							getOptionLabel={(option) => option.name}
							renderInput={(params) => (
								<TextField
									{...params}
									label={translate(
										"resources.moderation_entries.label.debate_filter",
									)}
								/>
							)}
							open={openGroupOptions}
							onOpen={() => {
								setOpenGroupOptions(true);
							}}
							onClose={() => {
								setOpenGroupOptions(false);
							}}
							onChange={(event, newValue) => {
								setGroupId(newValue.identifier);
							}}
							inputValue={groupOptionsInput}
							onInputChange={(event, newInputValue) => {
								setGroupOptionsInput(newInputValue);
								fetchGroupOptions(newInputValue);
							}}
						/>
					</FormControl>
					<Button onClick={resetFilters} className={classes.resetFilters}>
						{translate("resources.moderation_entries.label.reset_filters")}
					</Button>
				</div>
				<Link
					to={"/moderation_entries/moderation"}
					state={{
						language: currentLang,
						moderable_type: moderableType === "Report" ? "" : moderableType,
						group_type: groupType,
						group_id: groupId,
						is_reported: isReported,
					}}
					className={classes.moderationButton}
				>
					{translate("resources.moderation_entries.label.start_moderation")}{" "}
					<ArrowForwardIcon
						height={22}
						width={22}
						className={classes.arrowIcon}
					/>
				</Link>
			</div>
		</>
	);
};

export default ModerationLobby;
